import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { sendEmailVerification, signOut as fbSignOut } from "firebase/auth";
import { AuthShell } from "../components/auth-shell";
import { useAuthCtx } from "../lib/auth-provider";
import { claimTrial } from "../lib/callables";
import { decideAndRedirect } from "../lib/post-auth";
import { auth } from "../firebase";

const POLL_INTERVAL_MS = 5000;
const POLL_BACKOFF_MAX_MS = 60_000;

export function VerifyEmailPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user, loading, refresh } = useAuthCtx();
  const { t } = useTranslation();

  const [statusKey, setStatusKey] = useState<string>("verifyEmail.waiting");
  const [resentAt, setResentAt] = useState<number | null>(null);
  const [resending, setResending] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const claimedRef = useRef(false);
  // Stash params/nav/refresh in refs so the polling effect can call them
  // without listing them as deps (which would otherwise re-arm the poller
  // on every render and stack concurrent requests).
  const paramsRef = useRef(params);
  const navRef = useRef(nav);
  const refreshRef = useRef(refresh);
  useEffect(() => { paramsRef.current = params; }, [params]);
  useEffect(() => { navRef.current = nav; }, [nav]);
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);

  // No user? Bounce back to login (preserving query).
  useEffect(() => {
    if (!loading && !user) {
      const q = params.toString();
      nav(`/login${q ? "?" + q : ""}`, { replace: true });
    }
  }, [loading, user, params, nav]);

  // Polling loop: reload the user, check emailVerified, claim trial and redirect.
  // Uses a self-rescheduling setTimeout (not setInterval) so successive requests
  // can never overlap — the next tick is only queued after the previous one
  // completes. Stable deps via uid + refs prevents re-arming on every render.
  const uid = user?.uid;
  useEffect(() => {
    if (!uid) return;

    let cancelled = false;
    let timeoutId: number | undefined;
    let delay = POLL_INTERVAL_MS;

    async function schedule() {
      if (cancelled) return;
      timeoutId = window.setTimeout(tick, delay);
    }

    async function tick() {
      if (cancelled) return;
      try {
        await auth.currentUser?.reload();
        // Successful reload — reset backoff to the base interval.
        delay = POLL_INTERVAL_MS;
      } catch (e: unknown) {
        console.warn("[verify-email] reload failed", e);
        const code = (e as { code?: string })?.code;
        // Identity Toolkit rate-limits aggressively. Back off exponentially
        // up to 1 minute on QUOTA_EXCEEDED / too-many-requests so we stop
        // hammering the API instead of stacking failures.
        if (code === "auth/too-many-requests" || /QUOTA_EXCEEDED|too[-_ ]many[-_ ]requests/i.test(String(e))) {
          delay = Math.min(delay * 2, POLL_BACKOFF_MAX_MS);
          console.warn("[verify-email] backing off to", delay, "ms");
        }
        if (!cancelled) void schedule();
        return;
      }

      const current = auth.currentUser;
      if (!current || cancelled) return;

      if (current.emailVerified && !claimedRef.current) {
        claimedRef.current = true;
        setStatusKey("verifyEmail.activating");
        try {
          await claimTrial({});
          await current.getIdToken(true);
          await refreshRef.current();
        } catch (e) {
          console.error("[verify-email] claimTrial failed", e);
          setErrorKey("verifyEmail.errors.claimFailed");
          claimedRef.current = false;
          if (!cancelled) void schedule();
          return;
        }
        if (cancelled) return;
        await decideAndRedirect({
          user: current,
          params: paramsRef.current,
          nav: navRef.current,
          onSsoError: () => setErrorKey("auth.errors.ssoMintFailed"),
        });
        return;
      }

      // Not verified yet — schedule the next poll.
      if (!cancelled) void schedule();
    }

    // Kick off the first tick immediately, then loop via schedule().
    void tick();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [uid]);

  async function onResend() {
    if (!auth.currentUser) return;
    setResending(true);
    setErrorKey(null);
    try {
      await sendEmailVerification(auth.currentUser);
      setResentAt(Date.now());
    } catch (e) {
      console.warn("[verify-email] resend failed", e);
      setErrorKey("verifyEmail.errors.resendFailed");
    } finally {
      setResending(false);
    }
  }

  async function onWrongEmail() {
    // Sign out + send the user back to /signup so they can start over with the
    // correct email. The verify-email page is reachable only when authenticated,
    // so we have to drop the session first.
    await fbSignOut(auth);
    nav("/signup", { replace: true });
  }

  if (loading || !user) return null;

  const email = user.email ?? "";

  return (
    <AuthShell
      formTitle={t("verifyEmail.title")}
      formSub={t("verifyEmail.sub", { email })}
      hideLangToggle={!!user}
    >
      <div className="acx__stack acx__stack--lg">
        <div className="acx__alert acx__alert--info" role="status">
          {t(statusKey)}
        </div>

        <div className="acx__alert acx__alert--warning" role="note">
          {t("verifyEmail.spamHint")}
        </div>

        {errorKey && (
          <div className="acx__alert acx__alert--danger" role="alert">
            {t(errorKey)}
          </div>
        )}

        <button
          type="button"
          className="acx__btn acx__btn--primary acx__btn--full"
          onClick={onResend}
          disabled={resending}
        >
          {resending ? t("verifyEmail.resending") : t("verifyEmail.resend")}
        </button>

        {resentAt && (
          <p className="acx__auth-footer" style={{ marginTop: 0 }}>
            {t("verifyEmail.resent")}
          </p>
        )}

        <p className="acx__auth-footer" style={{ marginTop: 0, textAlign: "center" }}>
          {t("verifyEmail.wrongEmail")}{" "}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onWrongEmail();
            }}
          >
            {t("verifyEmail.wrongEmailLink")}
          </a>
        </p>
      </div>
    </AuthShell>
  );
}
